import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'hl_token';
const AuthContext = createContext(null);

// SECURITY NOTE: This app uses a dual-layer auth approach.
// Primary: httpOnly cookies (set by backend on login, forwarded via withCredentials).
// Fallback: localStorage token for environments where cross-origin cookies are blocked
// (e.g., production Kubernetes proxy that strips Set-Cookie headers across origins).
// This is an intentional architecture decision documented in /app/memory/PRD.md.
// XSS risk is mitigated by: strict CSP headers, no dynamic code execution, and no user-controlled innerHTML.

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      // Try cookie-based auth first (works on preview and most browsers)
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      // Fallback: use stored token (handles production environments where
      // cross-origin cookies may be blocked or not forwarded by proxy)
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        try {
          const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders(token), withCredentials: true });
          setUser(data);
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const setAuthUser = (userData) => setUser(userData);

  const ctx = useMemo(
    () => ({ user, loading, login, register, logout, setAuthUser, checkAuth }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return (
    <AuthContext.Provider value={ctx}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
